/**
 * Pure caret-positioning helpers shared by numeric inputs that reformat the
 * displayed text on every keystroke (money's thousands separators,
 * percentage's plain digits) while keeping the caret glued to the digit the
 * user was editing, rather than letting it jump to the end of the text.
 */

export const countDigits = (text: string) => (text.match(/\d/g) ?? []).length;

/** Position right after the `digits`-th digit in `formatted`. */
export const caretAfterDigits = (formatted: string, digits: number): number => {
  if (digits <= 0) return 0;
  let seen = 0;
  for (let index = 0; index < formatted.length; index++) {
    if (/\d/.test(formatted[index])) {
      seen++;
      if (seen === digits) return index + 1;
    }
  }
  return formatted.length;
};

/**
 * Backspace right after a formatting separator (e.g. a thousands dot) only
 * removes the separator, which the reformat puts straight back, so the key
 * would look dead. When an edit deleted characters but no digit, delete the
 * digit before the caret too. A no-op for text that has no separators to
 * begin with (e.g. plain digit strings).
 */
export const removeDigitBehindSeparator = (
  previousText: string,
  typedText: string,
  caret: number,
): string => {
  const deletedOnlySeparators =
    typedText.length < previousText.length &&
    countDigits(typedText) === countDigits(previousText);
  if (!deletedOnlySeparators) return typedText;
  const removedCount = previousText.length - typedText.length;
  const searchEnd = Math.max(caret - removedCount, 0);
  for (let index = searchEnd - 1; index >= 0; index--) {
    if (/\d/.test(typedText[index])) {
      return typedText.slice(0, index) + typedText.slice(index + 1);
    }
  }
  return typedText;
};
